import {
  hideError,
  isFileTooLarge,
  showError,
  canUploadMoreFiles,
  maybeHideFileLimitError,
  maybeShowFileLimitError,
} from '@/file_upload_util'
import {default as uswdsFileInput} from '@uswds/uswds/js/usa-file-input'
import {HtmxAfterRequestEvent} from '@/types/htmx'

// Shows the question's "Uploading…" badge on the question container during upload
const CF_FILE_UPLOADING_CLASS = 'cf-file-uploading'
// Disables nav and all .cf-disable-when-uploading elements
const CF_FILE_UPLOAD_IN_PROGRESS_CLASS = 'cf-file-upload-in-progress'
const CF_FILE_UPLOAD_CONTAINER_SELECTOR = '[data-cf-file-upload-container]'
const FILE_UPLOAD_HTMX_FAILURE = '[data-fileupload-error="request-failed"]'
const FILE_LIMIT_REACHED_ERROR_SELECTOR =
  '[data-fileupload-error="file-limit-reached"]'
const FILE_INPUT_SELECTOR = 'input[type=file]'

// Track the number of file uploads in progress to prevent navigating away
let fileUploadsInProgress = 0

export const init = () => {
  if (!document.querySelector(CF_FILE_UPLOAD_CONTAINER_SELECTOR)) {
    return
  }

  window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
    if (fileUploadsInProgress > 0) {
      e.preventDefault()
      // Deprecated in favor of preventDefault() but included for legacy browser support
      e.returnValue = true
    }
  })

  document.body.addEventListener('htmx:beforeRequest', (event) => {
    const fileInput = event.detail.elt
    if (!isCfFileUploadInput(fileInput)) {
      return
    }
    const fileUploadContainer = fileInput.closest(
      CF_FILE_UPLOAD_CONTAINER_SELECTOR,
    )

    // We validate both on the beforeRequest and onchange so that we block the request
    // to the server if the client invalidates the upload
    if (!validateFileUploadQuestion(fileInput)) {
      maybeShowFileLimitError(
        fileUploadContainer as HTMLElement,
        fileInput,
        fileUploadContainer?.querySelector<HTMLElement>(
          FILE_LIMIT_REACHED_ERROR_SELECTOR,
        ) as HTMLElement,
      )
      event.preventDefault()
      return
    }

    if (fileUploadContainer) {
      hideError(
        fileUploadContainer.querySelector<HTMLElement>(
          FILE_UPLOAD_HTMX_FAILURE,
        ),
        fileInput,
      )
      fileUploadContainer.classList.add(CF_FILE_UPLOADING_CLASS)
    }

    fileUploadsInProgress++
    document.body.classList.add(CF_FILE_UPLOAD_IN_PROGRESS_CLASS)
    toggleDisabledState()
  })

  document.body.addEventListener('htmx:afterRequest', (event) => {
    if (!isCfFileUploadInput(event.detail.elt)) {
      return
    }

    const fileUploadContainer = event.detail.elt.closest(
      CF_FILE_UPLOAD_CONTAINER_SELECTOR,
    )

    fileUploadsInProgress--
    if (fileUploadsInProgress <= 0) {
      fileUploadsInProgress = 0
      document.body.classList.remove(CF_FILE_UPLOAD_IN_PROGRESS_CLASS)
    }
    if (fileUploadContainer) {
      fileUploadContainer.classList.remove(CF_FILE_UPLOADING_CLASS)
    }
    toggleDisabledState()
    if (event.detail.successful) {
      if (fileUploadContainer) {
        hideError(
          fileUploadContainer.querySelector<HTMLElement>(
            FILE_UPLOAD_HTMX_FAILURE,
          ),
          event.detail.elt,
        )
      }
      resetFileInput(event)
    } else if (fileUploadContainer && !event.detail.successful) {
      showError(
        fileUploadContainer.querySelector<HTMLElement>(
          FILE_UPLOAD_HTMX_FAILURE,
        ),
        event.detail.elt,
      )
    }
  })

  document.body.addEventListener('htmx:afterSwap', (event) => {
    toggleDisabledState()
    const fileUploadContainer = event.detail.elt.closest(
      CF_FILE_UPLOAD_CONTAINER_SELECTOR,
    ) as HTMLElement

    if (fileUploadContainer) {
      const fileInput = fileUploadContainer.querySelector<HTMLInputElement>(
        FILE_INPUT_SELECTOR,
      ) as HTMLInputElement
      const fileLimitReachedErrorContainer =
        fileUploadContainer.querySelector<HTMLElement>(
          FILE_LIMIT_REACHED_ERROR_SELECTOR,
        ) as HTMLElement

      maybeHideFileLimitError(
        fileUploadContainer,
        fileInput,
        fileLimitReachedErrorContainer,
      )
    }
  })

  document.body.addEventListener('change', (event) => {
    if (isCfFileUploadInput(event.target)) {
      validateFileUploadQuestion(event.target)
    }
  })
}

/**
 * Validates the file upload question, showing an error if no file has been uploaded
 * and hiding the error otherwise.
 *
 * @returns true if a file was uploaded and false otherwise.
 */
const validateFileUploadQuestion = (fileInput: HTMLInputElement): boolean => {
  if (!fileInput || fileInput.type !== 'file') return false
  const fileUploadContainer = fileInput.closest(
    CF_FILE_UPLOAD_CONTAINER_SELECTOR,
  ) as HTMLElement
  if (!fileUploadContainer) return false

  const isFileUploaded = fileInput.value !== ''
  const canUploadMore = canUploadMoreFiles(fileUploadContainer)
  const fileNotSelectedErrorDiv =
    fileUploadContainer.querySelector<HTMLElement>(
      '[data-fileupload-error="required"]',
    )
  if (!isFileUploaded && canUploadMore) {
    showError(fileNotSelectedErrorDiv, fileInput)
  } else {
    hideError(fileNotSelectedErrorDiv, fileInput)
  }

  const isFileTooLargeResult = isFileTooLarge(fileInput)
  const fileTooLargeErrorDiv = fileUploadContainer.querySelector<HTMLElement>(
    '[data-fileupload-error="too-large"]',
  )

  if (isFileTooLargeResult) {
    showError(fileTooLargeErrorDiv, fileInput)
  } else {
    hideError(fileTooLargeErrorDiv, fileInput)
  }

  const isValid = isFileUploaded && !isFileTooLargeResult && canUploadMore
  if (isValid) {
    fileUploadContainer
      .querySelectorAll<HTMLElement>('.cf-question-error-message')
      .forEach((el) => (el.hidden = true))
    hideError(
      fileUploadContainer.querySelector<HTMLElement>(FILE_UPLOAD_HTMX_FAILURE),
      fileInput,
    )
  }
  // A valid file upload question is one that has an uploaded file that isn't too large.
  return isValid
}

const isCfFileUploadInput = (
  elt: EventTarget | null,
): elt is HTMLInputElement =>
  elt instanceof HTMLInputElement && elt.type === 'file'

const toggleDisabledState = () => {
  const elements = document.querySelectorAll('.cf-disable-when-uploading')
  elements.forEach((element) => {
    if (fileUploadsInProgress > 0) {
      element.setAttribute('disabled', '')
      element.setAttribute('aria-disabled', 'true')
    } else {
      element.removeAttribute('disabled')
      element.removeAttribute('aria-disabled')
    }
  })
}

const resetFileInput = (event: HtmxAfterRequestEvent) => {
  const fileUploadContainer = event.detail.elt.closest(
    CF_FILE_UPLOAD_CONTAINER_SELECTOR,
  ) as HTMLElement
  if (!fileUploadContainer) return

  const fileInput = fileUploadContainer.querySelector<HTMLInputElement>(
    FILE_INPUT_SELECTOR,
  ) as HTMLInputElement
  if (fileInput) {
    fileInput.value = ''
  }
  uswdsFileInput.off(fileUploadContainer)
  uswdsFileInput.on(fileUploadContainer)
}
